import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import {
  createAdminProduct,
  getAdminBrands,
  getAdminCategories,
  type AdminProductType,
} from "@/lib/admin-api-client";
import { flattenCategoryTree } from "@/components/admin/adminCategoryTree";
import { describeAdminError } from "@/components/admin/adminErrors";

const INPUT_CLASS = "w-full rounded-sm border border-line px-3 py-2 text-sm text-ink";

/**
 * `/admin/catalogue/produits/nouveau` — équivalent de
 * apps/web/app/(admin)/admin/catalogue/produits/nouveau/page.tsx : création
 * d'un produit. Crée systématiquement UNE SEULE variante par défaut avec le
 * prix saisi ici (`AdminProductsService.create()`, côté API) — pas d'éditeur
 * multi-variantes (couleur/taille…) à ce lot, cf. `admin-api-client.ts`.
 *
 * Validation client répliquée à l'identique de `createProductAction`
 * (`lib/admin-catalogue-actions.ts`) : nom + racine SKU obligatoires, prix
 * entier ≥ 0. Écart assumé : au lieu de `redirect(...?error=...)`, l'erreur
 * est affichée en place (état local) — pas de navigation nécessaire pour la
 * corriger. En cas de succès, navigation vers la fiche du produit créé avec
 * un `state` React Router (`{ success: true }`) au lieu du `?success=1` de
 * l'original — équivalent fonctionnel, lu une seule fois par
 * `AdminCatalogueProduitDetailPage`.
 */
export function AdminCatalogueProduitNouveauPage() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const brandsQuery = useQuery({ queryKey: ["admin", "brands"], queryFn: () => getAdminBrands() });
  const categoriesQuery = useQuery({ queryKey: ["admin", "categories"], queryFn: () => getAdminCategories() });

  const createMutation = useMutation({
    mutationFn: (input: Parameters<typeof createAdminProduct>[1]) => createAdminProduct(undefined, input),
    onSuccess: (product) => {
      navigate(`/admin/catalogue/produits/${product.id}`, { state: { success: true } });
    },
    onError: (err) => setFormError(describeAdminError(err)),
  });

  function parsePriceMinor(raw: string): number | null {
    if (!raw) return null;
    const value = Number.parseInt(raw, 10);
    if (!Number.isInteger(value) || value < 0 || String(value) !== raw) return null;
    return value;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const formData = new FormData(event.currentTarget);
    const name = String(formData.get("name") ?? "").trim();
    const skuRoot = String(formData.get("skuRoot") ?? "").trim();
    const priceRaw = String(formData.get("priceMinor") ?? "").trim();
    const priceMinor = parsePriceMinor(priceRaw);

    if (!name || !skuRoot) {
      setFormError("Le nom et la racine SKU sont obligatoires.");
      return;
    }
    if (priceMinor === null) {
      setFormError("Le prix doit être un entier positif (FCFA).");
      return;
    }

    const slug = String(formData.get("slug") ?? "").trim();
    const brandId = String(formData.get("brandId") ?? "").trim();
    const primaryCategoryId = String(formData.get("primaryCategoryId") ?? "").trim();
    const descriptionShort = String(formData.get("descriptionShort") ?? "").trim();
    const descriptionLong = String(formData.get("descriptionLong") ?? "").trim();
    const type = String(formData.get("type") ?? "").trim();

    createMutation.mutate({
      name,
      skuRoot,
      priceMinor,
      type: (type as AdminProductType | undefined) || undefined,
      slug: slug || undefined,
      brandId: brandId || undefined,
      primaryCategoryId: primaryCategoryId || undefined,
      descriptionShort: descriptionShort || undefined,
      descriptionLong: descriptionLong || undefined,
    });
  }

  const brands = brandsQuery.data ?? [];
  const flatCategories = flattenCategoryTree(categoriesQuery.data ?? []);

  return (
    <div>
      <div className="flex items-center gap-3">
        <Link to="/admin/catalogue/produits" className="text-sm font-medium text-navy hover:underline">
          ← Produits
        </Link>
      </div>

      <h1 className="mt-2 font-display text-2xl font-semibold text-ink">Nouveau produit</h1>
      <p className="mt-1 text-sm text-muted">
        Une variante par défaut est créée automatiquement avec le prix saisi ci-dessous.
      </p>

      {formError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{formError}</p>
      )}

      <form onSubmit={handleSubmit} className="mt-6 max-w-2xl space-y-5 rounded-sm border border-line bg-white p-6">
        <Field label="Nom" htmlFor="name" required>
          <input id="name" name="name" type="text" required className={INPUT_CLASS} />
        </Field>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Racine SKU" htmlFor="skuRoot" required hint="Sert aussi de SKU à la variante par défaut.">
            <input id="skuRoot" name="skuRoot" type="text" required className={INPUT_CLASS} />
          </Field>

          <Field label="Slug (optionnel)" htmlFor="slug" hint="Généré depuis le nom si laissé vide.">
            <input id="slug" name="slug" type="text" className={INPUT_CLASS} />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Prix (FCFA)" htmlFor="priceMinor" required>
            <input id="priceMinor" name="priceMinor" type="number" min={0} step={1} required className={INPUT_CLASS} />
          </Field>

          <Field label="Type" htmlFor="type">
            <select id="type" name="type" defaultValue="SIMPLE" className={INPUT_CLASS}>
              <option value="SIMPLE">Simple</option>
              <option value="VARIABLE">Variable</option>
              <option value="BUNDLE">Lot</option>
              <option value="GIFT_CARD">Carte cadeau</option>
              <option value="SAMPLE">Échantillon</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <Field label="Marque (optionnel)" htmlFor="brandId">
            <select id="brandId" name="brandId" defaultValue="" className={INPUT_CLASS}>
              <option value="">— Aucune —</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Catégorie principale (optionnel)" htmlFor="primaryCategoryId">
            <select id="primaryCategoryId" name="primaryCategoryId" defaultValue="" className={INPUT_CLASS}>
              <option value="">— Aucune —</option>
              {flatCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {"— ".repeat(cat.level)}
                  {cat.name}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Description courte (optionnel)" htmlFor="descriptionShort">
          <textarea id="descriptionShort" name="descriptionShort" rows={2} className={INPUT_CLASS} />
        </Field>

        <Field label="Description longue (optionnel)" htmlFor="descriptionLong">
          <textarea id="descriptionLong" name="descriptionLong" rows={5} className={INPUT_CLASS} />
        </Field>

        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded-sm bg-navy px-5 py-2.5 text-sm font-medium text-cream hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {createMutation.isPending ? "Création…" : "Créer le produit"}
        </button>
      </form>
    </div>
  );
}

/** Style commun des champs de formulaire admin (mêmes classes que `/admin/stock`, dupliquées faute d'utilitaire partagé — fidèle à l'original). */
function Field({
  label,
  htmlFor,
  required,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="text-red-600"> *</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-muted">{hint}</p>}
    </div>
  );
}
